import WorkflowPacket from "@/components/documents/WorkflowPacket";
import React, { useEffect, useId, useRef, useState } from "react";
import { api, boutiqueOf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, Save, User, Briefcase, DollarSign, ShieldCheck, ClipboardCheck, Scale, Paperclip, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import ReviewsDisciplineTab from "@/components/ReviewsDisciplineTab";
import "./EmployeeFileDrawer.css";
import "./employee-document-categories.css";
import { EMPLOYEE_DOCUMENT_CATEGORIES, DOCUMENT_FILING_FILTERS, documentFilingCategory, filterEmployeeDocuments } from "./employeeDocumentCategories";

const TABS = [
  { key: "personal", label: "Personal", icon: User },
  { key: "employment", label: "Employment", icon: Briefcase },
  { key: "compensation", label: "Compensation", icon: DollarSign },
  { key: "license", label: "License", icon: ShieldCheck },
  { key: "compliance", label: "Compliance", icon: ClipboardCheck },
  { key: "documents", label: "Documents", icon: Paperclip },
  { key: "reviews", label: "Reviews & Discipline", icon: Scale },
];

function Field({ label, children, span = 1 }) {
  const id = useId();
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="label-eyebrow mb-1 block" htmlFor={id}>{label}</label>
      {React.isValidElement(children) ? React.cloneElement(children, {id}) : children}
    </div>
  );
}

const Input = (p) => (
  <input {...p} className={`w-full px-3 py-1.5 rounded-2xl subtle-border text-xs outline-none focus:border-stone-400 disabled:bg-stone-50 ${p.className || ""}`} />
);

const Select = ({ options, ...p }) => (
  <select {...p} className={`w-full px-3 py-1.5 rounded-2xl subtle-border text-xs outline-none focus:border-stone-400 disabled:bg-stone-50 bg-white ${p.className || ""}`}>
    <option value="">—</option>
    {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
  </select>
);

const Toggle = ({ value, disabled, onChange }) => (
  <div className="flex gap-1.5">
    {[["yes", true], ["no", false], ["—", null]].map(([lbl, v]) => (
      <button key={String(lbl)} disabled={disabled} onClick={() => onChange(v)}
        className={`text-[11px] px-3 py-1 rounded-full border transition-colors ${value === v ? "bg-stone-900 border-stone-900 text-white" : "border-stone-200 text-stone-600 hover:bg-stone-50"} disabled:opacity-50`}>
        {lbl}
      </button>
    ))}
  </div>
);

export default function EmployeeFileDrawer({ employeeId, onClose, readOnly = false, embedded = false }) {
  const { user } = useAuth();
  const panelRef = useRef(null);
  useEffect(() => {
    if (embedded) return;
    const previous = document.activeElement;
    panelRef.current?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, [embedded]);
  const closeWithKeyboard = (event) => { if (!embedded && event.key === "Escape") { event.stopPropagation(); onClose(); } };
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("personal");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = () => {
    setLoadError("");
    api.get(`/employee-file/${employeeId}`).then((r) => setData(r.data)).catch(() => setLoadError("Your employee file could not be loaded. Please try again."));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [employeeId]);

  if (!data) return <aside ref={panelRef} tabIndex={-1} onKeyDown={closeWithKeyboard} role={embedded ? "region" : "dialog"} aria-label="Employee file" className={`employee-file-panel employee-file-panel--loading ${embedded ? "employee-file-panel--embedded" : ""}`}>{!embedded && <button type="button" onClick={onClose}>Close file</button>}{loadError ? <><p role="alert">{loadError}</p><button type="button" onClick={load}>Try again</button></> : <p role="status">Loading your employee file…</p>}</aside>;
  const f = data.file || {};

  const isSelf = user?.user_id === employeeId;
  const isHR = user && ["owner", "coo", "cfo", "leadership", "district_leader"].includes(user.role);
  const isGM = user?.role === "boutique_manager" && user.boutique_code === data.boutique_code;
  const canEditFull = !readOnly && (isHR || isGM);
  const canEdit = !readOnly && (canEditFull || isSelf); // self can edit personal only (backend enforces)
  const meta = data.boutique_code ? boutiqueOf(data.boutique_code) : null;

  const set = (patch) => setData((d) => ({ ...d, file: { ...(d.file || {}), ...patch } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/employee-file/${employeeId}`, f);
      toast.success("Saved to file");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <aside ref={panelRef} tabIndex={-1} onKeyDown={closeWithKeyboard} role={embedded ? "region" : "dialog"} aria-label="Employee file" className={`employee-file-panel ${embedded ? "employee-file-panel--embedded" : ""}`} data-testid="emp-file-drawer">
      <div className="employee-file-panel__header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar className="employee-file-panel__portrait" name={data.name} picture={data.picture} boutique={data.boutique_code} size={44} />
          <div>
            <div className="font-serif-display text-lg text-stone-900">{data.name}</div>
            <div className="text-[11px] text-stone-400 flex items-center gap-1.5">
              {meta && <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot}`} />}
              {data.title || (data.role || "").replace(/_/g, " ")} · {meta?.short || "Rainbow"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button onClick={save} disabled={busy} data-testid="file-save" className="px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800 disabled:opacity-40 flex items-center gap-1">
              <Save size={12} strokeWidth={1.5} /> {busy ? "…" : "Save"}
            </button>
          )}
          {!embedded && <button type="button" aria-label="Close employee file" onClick={onClose} data-testid="file-close" className="p-2 rounded-full hover:bg-stone-100"><X size={16} strokeWidth={1.25} /></button>}
        </div>
      </div>

      {/* Tabs */}
      <div className="employee-file-panel__tabs" aria-label="Employee file sections">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} aria-pressed={tab === t.key} data-testid={`file-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${tab === t.key ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}>
            <t.icon size={12} strokeWidth={1.5} /> {t.label}
          </button>
        ))}
      </div>

      <div className="employee-file-panel__content">
        {tab === "personal" && (
          <div className="grid grid-cols-2 gap-4" data-testid="tab-content-personal">
            <Field label="Legal first"><Input value={f.legal_first || ""} disabled={!canEditFull} onChange={(e) => set({ legal_first: e.target.value })} /></Field>
            <Field label="Legal last"><Input value={f.legal_last || ""} disabled={!canEditFull} onChange={(e) => set({ legal_last: e.target.value })} /></Field>
            <Field label="Preferred name"><Input value={f.preferred_name || ""} disabled={!canEdit} onChange={(e) => set({ preferred_name: e.target.value })} /></Field>
            <Field label="Date of birth"><Input type="date" value={f.dob || ""} disabled={!canEditFull} onChange={(e) => set({ dob: e.target.value })} /></Field>
            <Field label="Street" span={2}><Input value={f.address_street || ""} disabled={!canEdit} onChange={(e) => set({ address_street: e.target.value })} /></Field>
            <Field label="City"><Input value={f.address_city || ""} disabled={!canEdit} onChange={(e) => set({ address_city: e.target.value })} /></Field>
            <Field label="State"><Input value={f.address_state || ""} disabled={!canEdit} onChange={(e) => set({ address_state: e.target.value })} /></Field>
            <Field label="ZIP"><Input value={f.address_zip || ""} disabled={!canEdit} onChange={(e) => set({ address_zip: e.target.value })} /></Field>
            <Field label="Personal phone"><Input value={f.phone_personal || ""} disabled={!canEdit} onChange={(e) => set({ phone_personal: e.target.value })} /></Field>
            <Field label="Personal email"><Input value={f.email_personal || ""} disabled={!canEdit} onChange={(e) => set({ email_personal: e.target.value })} /></Field>
            <Field label="Gender"><Input value={f.gender || ""} disabled={!canEditFull} onChange={(e) => set({ gender: e.target.value })} /></Field>
            <Field label="Ethnicity"><Input value={f.ethnicity || ""} disabled={!canEditFull} onChange={(e) => set({ ethnicity: e.target.value })} /></Field>

            <div className="col-span-2 pt-4 mt-2 border-t border-stone-100">
              <div className="label-eyebrow mb-3">Emergency contact</div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Name"><Input value={f.emergency_contact_name || ""} disabled={!canEdit} onChange={(e) => set({ emergency_contact_name: e.target.value })} /></Field>
                <Field label="Relation"><Input value={f.emergency_contact_relation || ""} disabled={!canEdit} onChange={(e) => set({ emergency_contact_relation: e.target.value })} /></Field>
                <Field label="Phone"><Input value={f.emergency_contact_phone || ""} disabled={!canEdit} onChange={(e) => set({ emergency_contact_phone: e.target.value })} /></Field>
              </div>
            </div>
          </div>
        )}

        {tab === "employment" && (
          <div className="grid grid-cols-2 gap-4" data-testid="tab-content-employment">
            <Field label="Hire date"><Input type="date" value={data.hire_date || ""} disabled className="opacity-70" /></Field>
            <Field label="Tenure"><Input value={data.years_tenure ? `${data.years_tenure} year(s)` : "new"} disabled className="opacity-70" /></Field>
            <Field label="Position title"><Input value={data.title || ""} disabled className="opacity-70" /></Field>
            <Field label="Employment type"><Select value={f.employment_type || ""} disabled={!canEditFull} options={["W-2","1099"]} onChange={(e) => set({ employment_type: e.target.value })} /></Field>
            <Field label="Status"><Select value={f.employment_status || ""} disabled={!canEditFull} options={["active","leave","terminated"]} onChange={(e) => set({ employment_status: e.target.value })} /></Field>
            <Field label="Rehire eligible"><Toggle value={f.rehire_eligible} disabled={!canEditFull} onChange={(v) => set({ rehire_eligible: v })} /></Field>
            {f.employment_status === "terminated" && (
              <>
                <Field label="Separation date"><Input type="date" value={f.separation_date || ""} disabled={!canEditFull} onChange={(e) => set({ separation_date: e.target.value })} /></Field>
                <Field label="Separation reason" span={2}><Input value={f.separation_reason || ""} disabled={!canEditFull} onChange={(e) => set({ separation_reason: e.target.value })} /></Field>
              </>
            )}
          </div>
        )}

        {tab === "compensation" && (
          <div className="grid grid-cols-2 gap-4" data-testid="tab-content-compensation">
            <Field label="Base hourly ($)"><Input type="number" step="0.01" value={f.base_hourly ?? ""} disabled={!canEditFull} onChange={(e) => set({ base_hourly: e.target.value ? +e.target.value : null })} /></Field>
            <Field label="Base salary ($/yr)"><Input type="number" step="100" value={f.base_salary ?? ""} disabled={!canEditFull} onChange={(e) => set({ base_salary: e.target.value ? +e.target.value : null })} /></Field>
            <Field label="Commission %"><Input type="number" step="0.1" value={f.commission_pct ?? ""} disabled={!canEditFull} onChange={(e) => set({ commission_pct: e.target.value ? +e.target.value : null })} /></Field>
            <Field label="Per-service flat ($)"><Input type="number" step="0.5" value={f.service_commission_flat ?? ""} disabled={!canEditFull} onChange={(e) => set({ service_commission_flat: e.target.value ? +e.target.value : null })} /></Field>
            <Field label="Bonus eligible"><Toggle value={f.bonus_eligible} disabled={!canEditFull} onChange={(v) => set({ bonus_eligible: v })} /></Field>

            <div className="col-span-2 pt-4 mt-2 border-t border-stone-100">
              <div className="label-eyebrow mb-2">Pay history</div>
              {(f.pay_history || []).length === 0 && <div className="text-xs text-stone-400 italic">No entries yet — raises/promotions will be logged here.</div>}
              <div className="space-y-1.5">
                {(f.pay_history || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs text-stone-700 p-2 rounded-lg bg-stone-50/60 border border-stone-100">
                    <span className="text-stone-400">{p.date}</span>
                    <span className="font-medium">${p.rate}</span>
                    <span className="text-stone-500 flex-1">{p.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "license" && (
          <div className="grid grid-cols-2 gap-4" data-testid="tab-content-license">
            <Field label="License #"><Input value={f.license_number || ""} disabled={!canEditFull} onChange={(e) => set({ license_number: e.target.value })} /></Field>
            <Field label="State"><Input value={f.license_state || ""} disabled={!canEditFull} maxLength={2} placeholder="FL" onChange={(e) => set({ license_state: e.target.value.toUpperCase() })} /></Field>
            <Field label="Issue date"><Input type="date" value={f.license_issue_date || ""} disabled={!canEditFull} onChange={(e) => set({ license_issue_date: e.target.value })} /></Field>
            <Field label="Expiry date"><Input type="date" value={f.license_expiry_date || ""} disabled={!canEditFull} onChange={(e) => set({ license_expiry_date: e.target.value })} /></Field>
            <div className="col-span-2 text-[11px] text-stone-400">Attach a license copy in the secure Documents tab. It is encrypted before storage and is never shared by public URL.</div>
            {f.license_expiry_date && (() => {
              const days = Math.round((new Date(f.license_expiry_date) - new Date()) / 86400000);
              return (
                <div className={`col-span-2 text-xs px-4 py-2 rounded-2xl ${days < 0 ? "bg-rose-50 text-rose-700 border border-rose-100" : days < 60 ? "bg-amber-50 text-amber-700 border border-amber-100" : "bg-emerald-50 text-emerald-700 border border-emerald-100"}`}>
                  {days < 0 ? `⚠ Expired ${Math.abs(days)} day(s) ago` : days < 60 ? `⚠ Expires in ${days} day(s) — renew soon` : `Valid · ${days} days remaining`}
                </div>
              );
            })()}
          </div>
        )}

        {tab === "compliance" && (
          <div className="grid grid-cols-2 gap-4" data-testid="tab-content-compliance">
            <Field label="I-9 on file"><Toggle value={f.i9_on_file} disabled={!canEditFull} onChange={(v) => set({ i9_on_file: v })} /></Field>
            <Field label="W-4 on file"><Toggle value={f.w4_on_file} disabled={!canEditFull} onChange={(v) => set({ w4_on_file: v })} /></Field>
            <Field label="Direct deposit"><Toggle value={f.direct_deposit_on_file} disabled={!canEditFull} onChange={(v) => set({ direct_deposit_on_file: v })} /></Field>
            <Field label="Background check"><Toggle value={f.background_check_passed} disabled={!canEditFull} onChange={(v) => set({ background_check_passed: v })} /></Field>
            <Field label="Non-compete signed"><Toggle value={f.non_compete_signed} disabled={!canEditFull} onChange={(v) => set({ non_compete_signed: v })} /></Field>
            <Field label="Handbook acknowledged"><Toggle value={f.handbook_ack} disabled={!canEditFull} onChange={(v) => set({ handbook_ack: v })} /></Field>
          </div>
        )}
        {tab === "documents" && (
          <EmployeeDocuments key={employeeId === "me" ? user.user_id : employeeId} employeeId={employeeId === "me" ? user.user_id : employeeId} canManage={canEditFull} />
        )}
        {tab === "reviews" && (
          <ReviewsDisciplineTab
            employee={{
              user_id: employeeId,
              id: employeeId,
              name: data.name,
              boutique_code: data.boutique_code,
              role: data.role,
            }}
            canEditFull={canEditFull}
          />
        )}
      </div>

      {!canEditFull && (
        <div className="px-6 pb-6 text-[11px] text-stone-400 italic">
          {readOnly ? "Personal and employment details are read-only here." : isSelf ? "You can update your personal contact info. Employment, compensation, and compliance sections are HR/GM-managed." : "Read-only for your role."}
        </div>
      )}
    </aside>
  );
}

export function EmployeeDocuments({ employeeId, canManage, categoryFilter, showCategoryNavigation = true }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [category, setCategory] = useState("employee_file");
  const fileRef = useRef();
  const requestVersion = useRef(0);
  const [canCategorize, setCanCategorize] = useState(false);
  const [filter, setFilter] = useState('all');
  const [filingCategory, setFilingCategory] = useState('');
  const [page, setPage] = useState(0);
  const [savingCategory, setSavingCategory] = useState('');
  const [loadError, setLoadError] = useState('');
  const selectedCategory = categoryFilter || filter;
  const filtered = filterEmployeeDocuments(documents, selectedCategory);
  const pageCount = Math.max(1, Math.ceil(filtered.length / 4));
  const currentPage = Math.min(page, pageCount - 1);
  useEffect(() => setPage(0), [selectedCategory, employeeId]);

  const load = async () => {
    const request = ++requestVersion.current;
    setLoading(true); setLoadError(''); setDocuments([]); setCanCategorize(false);
    try {
      const { data } = await api.get(`/employee-documents/${employeeId}`);
      if (request !== requestVersion.current) return;
      setDocuments(data.documents || []); setCanCategorize(data.can_categorize === true);
    } catch (e) { if (request === requestVersion.current) setLoadError(e.response?.data?.detail || "Documents could not be loaded."); }
    finally { if (request === requestVersion.current) setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); return () => { requestVersion.current += 1; }; }, [employeeId]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    body.append("category", category);
    if (filingCategory) body.append("filing_category", filingCategory);
    try {
      await api.post(`/employee-documents/${employeeId}`, body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document encrypted and stored");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Document upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (doc) => {
    try {
      const r = await api.get(doc.workflow_document_id ? `/document-workflows/${doc.workflow_document_id}/completion/${employeeId}/download?version=${doc.workflow_version}` : `/employee-documents/${employeeId}/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: doc.content_type }));
      const a = document.createElement("a"); a.href = url; a.download = doc.filename; a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  const organize = async (doc, value) => {
    setSavingCategory(doc.id);
    try {
      const { data } = await api.patch(`/employee-documents/${employeeId}/${doc.id}/filing`, { filing_category: value || null });
      setDocuments(items => items.map(item => item.id === doc.id ? data : item));
      toast.success('Document category saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Category was not saved.'); }
    finally { setSavingCategory(''); }
  };

  const assignPacket = async () => {
    setAssigning(true);
    try {
      await api.post(`/onboarding-documents/${employeeId}/assign`, {});
      toast.success("Onboarding packet assigned to this profile");
    } catch (e) { toast.error(e.response?.data?.detail || "Onboarding packet could not be assigned"); }
    finally { setAssigning(false); }
  };

  return (
    <div className="employee-documents" data-testid="tab-content-documents">
      <WorkflowPacket employeeId={employeeId} management={canManage}/>
      {showCategoryNavigation && <nav className="employee-document-categories" aria-label="Document categories">{DOCUMENT_FILING_FILTERS.map(([key,label]) => <button key={key} type="button" aria-current={selectedCategory === key ? 'page' : undefined} onClick={() => setFilter(key)}>{label}</button>)}</nav>}
      <div className="rounded-2xl border border-stone-100 bg-stone-50/50 p-4 text-xs text-stone-600 leading-relaxed">
        Files are encrypted before storage, available only to authorized HR/leadership or this boutique’s manager, and never exposed through a public link.
      </div>
      {canManage && <div className="flex items-end gap-3 mt-5">
        <Field label="Document type"><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. i9, license, offer" /></Field>
        <Field label="Filing category"><select value={filingCategory} onChange={e => setFilingCategory(e.target.value)}><option value="">Uncategorized</option>{EMPLOYEE_DOCUMENT_CATEGORIES.map(([key,label]) => <option value={key} key={key}>{label}</option>)}</select></Field>
        <input ref={fileRef} className="hidden" type="file" accept=".pdf,.docx,.jpg,.jpeg,.png,.webp" onChange={(e) => upload(e.target.files?.[0])} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="shrink-0 px-4 py-2 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800 disabled:opacity-40 flex items-center gap-1.5"><Upload size={12} />{uploading ? "Encrypting…" : "Upload"}</button>
      </div>}
      {canManage && <div className="mt-4 flex items-center justify-between gap-4 rounded-2xl border border-stone-100 bg-stone-50/50 p-4">
        <div><div className="text-xs font-medium text-stone-800">Onboarding packet</div><div className="mt-1 text-[11px] text-stone-500">Assign the current I-9, W-4, direct-deposit, and emergency-contact forms to this employee’s Profile.</div></div>
        <button onClick={assignPacket} disabled={assigning} className="shrink-0 px-4 py-2 rounded-full border border-stone-300 text-stone-700 text-xs hover:bg-white disabled:opacity-40">{assigning ? "Assigning…" : "Assign packet"}</button>
      </div>}
      <div className="mt-5 space-y-2">
        {loading && <div className="text-xs text-stone-400">Loading secure documents…</div>}
        {loadError && <div role="alert">{loadError}<button onClick={load}>Try again</button></div>}
        {!loading && !loadError && filtered.length === 0 && <p className="employee-documents__empty">{documents.length ? 'No documents in this category yet. Existing files are available under All documents and Uncategorized.' : 'No documents stored yet.'}</p>}
        {!loading && filtered.slice(currentPage * 4, currentPage * 4 + 4).map((doc) => <div key={doc.id} className="employee-documents__row">
          <Paperclip size={14} className="text-stone-400" />
          <div className="min-w-0 flex-1"><div className="text-xs text-stone-800 truncate">{doc.filename}</div><div className="text-[10px] text-stone-400">{doc.category.replace(/_/g, " ")} · {doc.workflow_document_id ? `Version ${doc.workflow_version} · ${doc.workflow_status === "awaiting_manager" ? "Awaiting GM countersignature" : "Filed"}` : `${Math.max(1, Math.round(doc.size / 1024))} KB`}</div></div>
          {canCategorize && !doc.workflow_document_id ? <label className="employee-documents__category"><span className="sr-only">Category for {doc.filename}</span><select aria-label={`Category for ${doc.filename}`} value={documentFilingCategory(doc) === 'uncategorized' ? '' : doc.filing_category} disabled={!!savingCategory} onChange={e => organize(doc, e.target.value)}><option value="">Uncategorized</option>{EMPLOYEE_DOCUMENT_CATEGORIES.map(([key,label]) => <option value={key} key={key}>{label}</option>)}</select></label> : <span className="employee-documents__category-label">{EMPLOYEE_DOCUMENT_CATEGORIES.find(([key]) => key === doc.filing_category)?.[1] || 'Uncategorized'}</span>}
          <button aria-label={`Download ${doc.filename}`} onClick={() => download(doc)} className="p-1.5 rounded-full hover:bg-stone-100 text-stone-500" title="Download securely"><Download size={13} /></button>
        </div>)}
      </div>
      {!loading && !loadError && filtered.length > 4 && <nav className="employee-documents__pagination" aria-label="Document pages"><button onClick={() => setPage(currentPage - 1)} disabled={currentPage === 0}>Previous</button><span>Page {currentPage + 1} of {pageCount}</span><button onClick={() => setPage(currentPage + 1)} disabled={currentPage + 1 >= pageCount}>Next</button></nav>}
    </div>
  );
}
